import { graphql } from '@/__generated__';
import { MacroDefinitionFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetMacrosByUniqueId = graphql(/* GraphQL */ `
  query Macros(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: MacroDefinitionFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        macros(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              meta
              name
              packageName
              macroSql
              runId
              arguments {
                name
              }
            }
          }
        }
      }
    }
  }
`);

type MacroByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId: number | null;
  filter?: Partial<MacroDefinitionFilter>;
  pause?: boolean;
};

/**
 * Macros are Definition Only
 */
export const useDiscoveryDefinitionMacros = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: MacroByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetMacrosByUniqueId,
      variables: { first, after, environmentId, filter, userId },
      pause: pause || !environmentId || !first || filter?.uniqueIds?.length === 0,
    },
    (data) => {
      const macros = data.environment.definition?.macros;
      const pageInfo = macros?.pageInfo;
      if (macros == null || pageInfo == null) return undefined;
      return {
        totalCount: macros.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.definition?.macros.edges.map((edge) => edge.node);
    },
    (data) => data.environment.definition?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
